const APPS = [
  { key: 'logo', icon: 'logov2', route: '/', label: 'Workspace' },
  { key: 'studio', icon: 'layers', route: '/studio', label: 'Studio' },
  { key: 'marketplace', icon: 'grid', route: '/marketplace', label: 'Marketplace' },
  { key: 'chat', icon: 'chat', route: '/chat', label: 'Chat' },
  { key: 'calendar', icon: 'date outline', route: '/calendar', label: 'Calendar' },
  { key: 'more', icon: 'arrowAngleDown', label: 'More' }
]

export const DROPDOWN_APPS = [
  ...APPS.filter((a) => a.key !== 'more'),
  { key: 'tickets', icon: 'check', route: '/tickets', label: 'Tickets' },
  { key: 'documents', icon: 'document', route: '/documents', label: 'Documents' },
  { key: 'meet', icon: 'video', route: '/meet', label: 'Meet' },
  { key: 'system', icon: 'settings', route: '/system', label: 'System' },
  { key: 'data', icon: 'database', route: '/data/projects', label: 'Data' }
]

// Resolve `/studio` to `/studio/<owner-slug>` for the active org. The
// auth wiring (workspace-project/functions/auth.js) sets `root.org` to
// the active org object whose canonical identifier is `org.slug`.
// Personal workspaces have no org, so fall back to the email-derived
// user slug. Last resort: `/dashboard`, which renders a workspace
// picker on its own. All routes are now local — the multi-app shell
// hosts canvas / marketplace / etc. as registered routes inside the
// same origin, so no cross-origin bouncing.
//
// Optimistic pick: when the user just clicked an org in
// AppWorkspaceSwitcher, `_pendingActiveOrgId` is set the same tick the
// trigger label flips. Without consulting it here the Studio link in
// the navbar would keep pointing at the OLD org's slug until the JWT
// refresh round-trip completes (1–5 s) — clicking Studio in that
// window navigates to the wrong studio. Resolve the pending org's
// slug from `jwtClaims.orgs` first so the whole header flips together.
const resolveStudioPath = (s) => {
  const root = s?.root
  const pendingId = root?._pendingActiveOrgId
  if (pendingId) {
    const pendingOrg = (root?.jwtClaims?.orgs || []).find((o) => o.id === pendingId)
    if (pendingOrg?.slug) return `/studio/${pendingOrg.slug}`
  }
  const orgSlug = root?.org?.slug
  if (orgSlug) return `/studio/${orgSlug}`
  const email = root?.currentUserEmail || ''
  const userSlug = email.split('@')[0] || ''
  if (userSlug) return `/studio/${userSlug}`
  return '/dashboard'
}

// Routes that belong to the workspace home surface — used by isIntranetRoute
// to decide whether to show the nav links bar instead of the global search.
export const WORKSPACE_PATHS = [
  '/',
  '/people',
  '/my',
  '/announcements',
  '/company',
  '/profile'
]
export const INTRANET_PATHS = WORKSPACE_PATHS

// State items without an own `route` (the "more" button) fall through
// to `s.root.route` via the store-proxy chain, so a plain `s.route`
// read would return whatever ambient value is on root. hasOwnProperty
// guards those items from matching.
const _isActive = (s) => {
  if (!s || !Object.prototype.hasOwnProperty.call(s, 'route')) return false
  const path = s.root?.currentPath || '/'
  return s.route === '/'
    ? WORKSPACE_PATHS.includes(path)
    : path.startsWith(s.route)
}

// hasOwnProperty guard: items with no own `route` (the "more" button)
// fall through to `s.root.route` via the store-proxy chain — without
// the guard `_hrefFor` would return the current page's path as the
// More-button href, making it look like a real link.
//
// All nav links are LOCAL routes — the multi-app shell mounts every
// sub-app (canvas / marketplace / etc.) as a registered route in the
// same SPA. Studio still resolves dynamically to `/studio/<slug>`.
const _hrefFor = (el, s) => {
  if (s?.key === 'studio') return resolveStudioPath(s)
  if (!s || !Object.prototype.hasOwnProperty.call(s, 'route')) return undefined
  return s.route || undefined
}

const _btnLabel = (s) =>
  s?.label || (s?.key ? s.key.charAt(0).toUpperCase() + s.key.slice(1) : '')

const _unreadFor = (s) => Number(s?.root?.navUnreadCounts?.[s?.key] || 0)

const _closeContainingDropdown = (el) => {
  let dropdown = el
  while (dropdown && !dropdown.isDropdownRoot) dropdown = dropdown.parent
  if (!dropdown) return
  dropdown.forcedVisible = false
  dropdown.update({
    style: { opacity: '0', visibility: 'hidden', pointerEvents: 'none' }
  })
}

const _toggleMoreDropdown = (e, el) => {
  e.preventDefault()
  let header = el
  while (header && !header.AppNavbarDropdown) header = header.parent
  const dropdown = header?.AppNavbarDropdown
  if (!dropdown) return
  const newVal = !dropdown.forcedVisible
  dropdown.forcedVisible = newVal
  dropdown.update({
    style: {
      opacity: newVal ? '1' : '0',
      visibility: newVal ? 'visible' : 'hidden',
      pointerEvents: newVal ? 'auto' : 'none'
    }
  })
}

export const AppNavbarDropdownItem = {
  extends: 'Link',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Z1 A',
  round: 'Z',
  cursor: 'pointer',
  theme: 'tertiary',
  width: '100%',
  textDecoration: 'none',

  href: (el, s) => _hrefFor(el, s),
  // Icon-only nav items have no text, so set both `title` (native
  // tooltip + status bar) and `aria-label` (screen readers) directly
  // on `attr`. Top-level `aria: { label }` does NOT promote to the
  // `aria-label` attribute in v3.14 — verified against live DOM.
  attr: {
    title: (el, s) => _btnLabel(s),
    'aria-label': (el, s) => _btnLabel(s)
  },

  onClick: (e, el, s) => {
    _closeContainingDropdown(el)
    e.preventDefault()
    el.router(_hrefFor(el, s), el.getRoot())
  },

  Icon: { name: '{{icon}}', boxSize: 'A A' },
  Label: { tag: 'span', fontSize: 'Z2', text: '{{label}}' }
}

export const AppNavbarButton = {
  // Multi-extends: NavbarButton supplies the icon-rendering chain
  // (IconText → Icon child resolved from `icon: '{{ icon }}'`) plus
  // hover tooltip wiring; Link supplies tag: 'a' + attr.href so the
  // button renders as a real anchor with native middle-click + status
  // bar URL. Custom onClick below overrides Link's routerOnClick.
  extends: ['NavbarButton', 'Link'],
  borderWidth: '0',
  round: '100%',
  position: 'relative',
  cursor: 'pointer',
  textDecoration: 'none',
  // Active state is driven by reactive factories instead of `isActive` +
  // `.isActive` because smbls v3.14 only wires reactive effects for
  // function props that are in the CSS_PROPS_REGISTRY. `isActive` is a
  // custom boolean, so `applyConditionals` runs the check ONCE at
  // creation and freezes it. `background` and `color` ARE in the CSS
  // registry, so their factories re-run whenever `root.currentPath`
  // updates via `locationchange`.
  //
  // Theme-aware tokens: `overlayStrong` (active) and `overlay` (hover)
  // are semantic colors that resolve correctly in both light and dark
  // mode. Previously used `black.4` / `black.2` literals which were
  // near-invisible against the dark navbar background.
  background: (el, s) => (_isActive(s) ? 'overlayStrong' : 'transparent'),
  color: (el, s) => (_isActive(s) ? 'title' : 'caption'),
  ':hover': { color: 'title', background: 'overlay' },

  href: (el, s) => _hrefFor(el, s),
  // Custom navbar tooltip uses setNavbarTooltipPosition (onMouseover);
  // we still want the native title + aria-label for accessibility +
  // middle-click status bar. el.text is empty (icon-only) — top-level
  // `aria: { label }` does NOT promote to `aria-label` in v3.14, so
  // both must live on `attr`.
  attr: {
    title: (el, s) => _btnLabel(s),
    'aria-label': (el, s) => _btnLabel(s)
  },
  icon: '{{ icon }}',

  // Brand's onMouseleave calls Tooltip.update() which can't override
  // the inline styles setNavbarTooltipPosition sets; trigger the
  // slide-out animation directly instead.
  onMouseleave: (ev, el, s) => {
    if (s.hover) {
      s.hover = false
      el.call('setNavbarTooltipPosition', { visible: false })
    }
  },

  onClick: (e, el, s) => {
    if (s.key === 'more') return _toggleMoreDropdown(e, el)
    e.preventDefault()
    el.router(_hrefFor(el, s), el.getRoot())
  },

  UnreadBadge: {
    hide: (el, s) => _unreadFor(s) === 0,
    position: 'absolute',
    top: '0',
    right: '0',
    width: 'Y',
    height: 'Y',
    round: '50%',
    background: 'red',
    pointerEvents: 'none'
  }
}

export const AppNavbar = {
  extends: 'Navbar',
  flow: 'x',
  align: 'center space-around',
  gap: 'Z+V',
  padding: 'V Z',
  userSelect: 'none',
  theme: 'quaternary',
  round: 'C1',

  hide: () => /^\/(login|signin|signup|reset-password|forgot-password|verify-email|signup-confirmation)/.test(window.location.pathname),

  childExtends: 'AppNavbarButton',
  childrenAs: 'state',
  children: APPS
}
